import { graphql } from "gatsby"
import React from "react"
import { Link } from "gatsby"
import { Layout } from "@bw/layouts"
import { Seo, Section, Title, CmsImage } from "@bw/bits"
import { PageHeader, Teasers } from "@bw/modules"
import { Breadcrumbs, SidebarLayout, Magazine, Teaser } from "@bw/partials"
import { Content, Footer, Header } from "../components/modules/mapModules.jsx"
import { useTranslation } from "react-i18next"

const MagazineTemplate = ({ data, pageContext, location }) => {
  const magazine = data.collectionMagazines
  const {
    mount,
    sidemenu,
    letsHearFromYou,
    allCollectionPages,
    subscribeToMagazine,
  } = data
  const related = magazine.healthTips
  const archives = data.allCollectionMagazines.nodes
  const { t } = useTranslation()

  let parole = undefined

  if (letsHearFromYou.letsHearFromYou.length > 0) {
    parole = allCollectionPages.nodes.find(
      node => node.statamicId === letsHearFromYou.letsHearFromYou[0]
    )
  }

  let register = undefined

  if (subscribeToMagazine.subscribeToMagazine.length > 0) {
    register = allCollectionPages.nodes.find(
      node => node.statamicId === subscribeToMagazine.subscribeToMagazine[0]
    )
  }

  const mountImage = mount.header[0]?.image

  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: mount.title,
      to: mount.slug,
    },
    {
      title: magazine.title,
      to: magazine.slug,
    },
  ]

  return (
    <Layout {...{ pageContext }} color={magazine.color?.value}>
      <Seo
        url={location.href}
        title={magazine.metaTitle || magazine.title}
        description={magazine.metaDescription}
        image={magazine.image?.url}
        meta={[
          {
            name: "robots",
            content: magazine.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <PageHeader
        title="Nos publications"
        subtitle="Restez informés grâce à nos différentes publications."
        image={
          mountImage ? (
            <CmsImage src={mountImage} alt={mountImage.alt} />
          ) : undefined
        }
        pictogram={
          <svg className="icon">
            <use xlinkHref="/sprite-v2.svg#cross" />
          </svg>
        }
      />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <SidebarLayout sidemenu={sidemenu.nodes}>
        <Section>
          <Magazine
            image={<CmsImage src={magazine?.cover} alt={magazine?.title} />}
            title={magazine?.title}
            excerpt={t("magazine.excerpt")}
            color={magazine?.color?.value}
            button={t("magazine.button")}
            subscribeUrl={register?.slug}
            pdfUrl={magazine.slug ? magazine.slug + "/pdf" : null}
            paroleUrl={parole?.slug}
          />
        </Section>
        {related && related.length > 0 && (
          <Section margin="100px 0">
            <Title title={t("magazine.inThisMagazine")} size="h3" />
            <Teasers>
              {related.map((item, i) => {
                return (
                  <Teaser
                    smaller
                    key={i}
                    title={item.title}
                    excerpt={item.teaserExcerpt}
                    to={item.slug}
                    image={<CmsImage src={item.teaserImage} alt={item.title} />}
                  />
                )
              })}
            </Teasers>
          </Section>
        )}
        {archives && archives.length > 0 && (
          <Section>
            <Title title={t("magazine.archives")} size="h3" />
            <Teasers numberOfColumns={4}>
              {archives.map((item, i) => {
                return (
                  <Link key={i} to={item.slug}>
                    <CmsImage src={item.cover} alt={item.title} />
                  </Link>
                )
              })}
            </Teasers>
          </Section>
        )}
      </SidebarLayout>
      <Footer modules={magazine.footer} />
    </Layout>
  )
}

export default MagazineTemplate

export const magazineQuery = graphql`
  query magazineQuery(
    $id: String!
    $siblings: [String]
    $hasSiblings: Boolean = false
  ) {
    sidemenu: allCollectionPages(filter: { statamicId: { in: $siblings } }) {
      nodes @include(if: $hasSiblings) {
        slug
        title
      }
    }
    collectionMagazines(id: { eq: $id }) {
      metaDescription
      image {
        url
      }
      metaTitle
      doNotIndex
      title
      slug
      color {
        value
      }
      pdf {
        url
      }
      cover {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 329, height: 428)
          }
        }
      }
      healthTips {
        title
        slug
        teaserExcerpt
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 254, height: 139)
            }
          }
        }
      }
      footer {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
      }
    }
    allCollectionMagazines(
      filter: { id: { ne: $id } }
      sort: { publishDate: DESC }
    ) {
      nodes {
        title
        slug
        publishDate
        cover {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 187, height: 246)
            }
          }
        }
      }
    }
    mount: collectionPages(mount: { handle: { eq: "magazines" } }) {
      slug
      title
      header {
        ... on PageHeaderModule {
          image {
            url
          }
        }
      }
    }
    letsHearFromYou: globals(
      handle: { eq: "pages" }
      letsHearFromYou: { ne: null }
    ) {
      id
      letsHearFromYou
    }
    subscribeToMagazine: globals(
      handle: { eq: "pages" }
      subscribeToMagazine: { ne: null }
    ) {
      id
      subscribeToMagazine
    }
    allCollectionPages {
      nodes {
        statamicId
        title
        slug
      }
    }
  }
`
